import React, { useEffect, useState } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import useSettings from "../../hooks/useSettings";
import { useWebInfo } from "../../context/WebInfoContext";
import api from "../../services/api";
import { toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";
import OnlyForSuperUser from '../../components/OnlyForSuperUser';
import useAuth from '../../hooks/useAuth.js';
import { i18n } from '../../translate/i18n.js';
import LogoManager from './LogoManager';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  sectionHeader: {
    background: theme.mode === 'dark' 
      ? 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' 
      : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  settingItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    transition: 'all 0.2s ease',
    height: '100%',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e3f2fd',
      borderColor: theme.mode === 'dark' ? theme.palette.primary.light : '#1976d2',
    },
  },
  settingLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  settingDescription: {
    fontSize: '11px',
    color: theme.palette.text.secondary || theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      '& fieldset': {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
  },
  loadingText: {
    fontSize: '12px',
    color: theme.palette.primary.main,
    fontStyle: 'italic',
  },
}));

// Componente helper para renderizar configuraciones individuales
const SettingItem = ({ 
  label, 
  description, 
  value, 
  onChange, 
  options, 
  loading, 
  classes,
  invertStatus = false
}) => {
  const getStatusChip = (val) => {
    // ✅ Variable isEnabled removida - no se usaba
    return (
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
        <FormControl className={classes.formControl} variant="outlined" style={{ flex: 1, minWidth: 150 }}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value}
            onChange={onChange}
            label={label}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {loading && (
            <FormHelperText className={classes.loadingText}>
              Actualizando...
            </FormHelperText>
          )}
        </FormControl>
      </Box>
    );
  };

  return (
    <Box className={classes.settingItem}>
      <Typography className={classes.settingLabel}>
        {label}
      </Typography>
      {description && (
        <Typography className={classes.settingDescription}>
          {description}
        </Typography>
      )}
      {getStatusChip(value)}
    </Box>
  );
};

export default function WebConfig() {
  const classes = useStyles();
  const { update } = useSettings();
  // ✅ CENTRALIZADO: Usar useWebInfo para cargar settings globales (evita duplicados)
  const { webInfo, updateWebInfo } = useWebInfo();

  const [currentUser, setCurrentUser] = useState({});
  const { getCurrentUserInfo } = useAuth();

  useEffect(() => {
    async function findData() {
      try {
        const user = await getCurrentUserInfo();
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [allowregister, setallowregister] = useState('disabled');
  const [loadingallowregister, setLoadingallowregister] = useState(false);

  const [viewregister, setviewregister] = useState('disabled');
  const [loadingviewregister, setLoadingviewregister] = useState(false);

  const [trial, settrial] = useState('3');
  const [loadingtrial, setLoadingtrial] = useState(false);

  const [hubNotificaMeToken, setHubNotificaMeToken] = useState("");
  const [loadingHubNotificaMeToken, setLoadingHubNotificaMeToken] = useState(false);

  // ✅ CENTRALIZADO: Usar valores de webInfo del hook (evita carga duplicada)
  // Estados locales solo para edición temporal antes de guardar
  const [websiteTitle, setWebsiteTitle] = useState(webInfo.title || "");
  const [websiteDescription, setWebsiteDescription] = useState(webInfo.description || "");
  const [websiteKeyword, setWebsiteKeyword] = useState(webInfo.keywords || "");
  const [companyName, setCompanyName] = useState(webInfo.companyName || "");
  const [loadingWebsiteTitle, setLoadingWebsiteTitle] = useState(false);
  const [loadingWebsiteDescription, setLoadingWebsiteDescription] = useState(false);
  const [loadingWebsiteKeyword, setLoadingWebsiteKeyword] = useState(false);
  const [loadingCompanyName, setLoadingCompanyName] = useState(false);

  // ✅ SINCRONIZAR: Actualizar estados locales cuando webInfo cambia (desde el hook)
  useEffect(() => {
    if (webInfo.title) setWebsiteTitle(webInfo.title);
    if (webInfo.description) setWebsiteDescription(webInfo.description);
    if (webInfo.keywords) setWebsiteKeyword(webInfo.keywords);
    if (webInfo.companyName) setCompanyName(webInfo.companyName);
  }, [webInfo]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // ✅ Cargar configuraciones desde la API (settings globales con companyId = 1)
        const globalSettingsKeys = ['allowregister', 'viewregister', 'trial', 'hubToken'];
        
        const settingsPromises = globalSettingsKeys.map(key =>
          api.get(`/settings/${key}`, { params: { companyId: 1 } }).catch(() => null)
        );

        const settingsResults = await Promise.all(settingsPromises);

        // ✅ allowregister
        if (settingsResults[0]?.data?.value !== undefined) {
          setallowregister(settingsResults[0].data.value);
        } else {
          // Fallback a window.settings si existe
          const allowregisterSetting = window.settings?.find((s) => s.key === 'allowregister');
          if (allowregisterSetting) {
            setallowregister(allowregisterSetting.value);
          }
        }

        // ✅ viewregister
        if (settingsResults[1]?.data?.value !== undefined) {
          setviewregister(settingsResults[1].data.value);
        } else {
          const viewregisterSetting = window.settings?.find((s) => s.key === 'viewregister');
          if (viewregisterSetting) {
            setviewregister(viewregisterSetting.value);
          }
        }

        // ✅ trial
        if (settingsResults[2]?.data?.value !== undefined) {
          settrial(settingsResults[2].data.value);
        } else {
          const trialSetting = window.settings?.find((s) => s.key === 'trial');
          if (trialSetting) {
            settrial(trialSetting.value);
          }
        }

        // ✅ hubToken
        if (settingsResults[3]?.data?.value !== undefined) {
          setHubNotificaMeToken(settingsResults[3].data.value);
        } else {
          const hubToken = window.settings?.find((s) => s.key === "hubToken");
          if (hubToken) {
            setHubNotificaMeToken(hubToken.value);
          }
        }
      } catch (error) {
        console.error('Error cargando settings globales:', error);
        // Fallback a window.settings si hay error
        if (Array.isArray(window.settings) && window.settings.length) {
          const allowregisterSetting = window.settings.find((s) => s.key === 'allowregister');
          if (allowregisterSetting) {
            setallowregister(allowregisterSetting.value);
          }
          const viewregisterSetting = window.settings.find((s) => s.key === 'viewregister');
          if (viewregisterSetting) {
            setviewregister(viewregisterSetting.value);
          }
          const trialSetting = window.settings.find((s) => s.key === 'trial');
          if (trialSetting) {
            settrial(trialSetting.value);
          }
          const hubToken = window.settings.find((s) => s.key === "hubToken");
          if (hubToken) {
            setHubNotificaMeToken(hubToken.value);
          }
        }
      }
    };
    
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleallowregister(value) {
    setallowregister(value);
    setLoadingallowregister(true);
    try {
      await update({
        key: 'allowregister',
        value,
      });
      toast.success(i18n.t("settings.success"));
      // ✅ Recargar valor desde la API para confirmar que se guardó
      const { data } = await api.get(`/settings/allowregister`, { params: { companyId: 1 } });
      if (data?.value !== undefined) {
        setallowregister(data.value);
      }
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando allowregister:', error);
    } finally {
      setLoadingallowregister(false);
    }
  }

  async function handleviewregister(value) {
    setviewregister(value);
    setLoadingviewregister(true);
    try {
      await update({
        key: 'viewregister',
        value,
      });
      toast.success(i18n.t("settings.success"));
      // ✅ Recargar valor desde la API para confirmar que se guardó
      const { data } = await api.get(`/settings/viewregister`, { params: { companyId: 1 } });
      if (data?.value !== undefined) {
        setviewregister(data.value);
      }
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando viewregister:', error);
    } finally {
      setLoadingviewregister(false);
    }
  }
  
  async function handletrial(value) {
    settrial(value);
    setLoadingtrial(true);
    try {
      await update({
        key: 'trial',
        value,
      });
      toast.success(i18n.t("settings.success"));
      // ✅ Recargar valor desde la API para confirmar que se guardó
      const { data } = await api.get(`/settings/trial`, { params: { companyId: 1 } });
      if (data?.value !== undefined) {
        settrial(data.value);
      }
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando trial:', error);
    } finally {
      setLoadingtrial(false);
    }
  }

  async function handleChangeHubNotificaMe(value) {
    setHubNotificaMeToken(value);

    if (value.length === 36) {
      setLoadingHubNotificaMeToken(true);
      await update({
        key: "hubToken",
        value,
      });
      toast.success(i18n.t("settings.success"));
      setLoadingHubNotificaMeToken(false);
    }
  }

  async function handleChangeWebsiteTitle(value) {
    setWebsiteTitle(value);
  }

  async function handleSaveWebsiteTitle() {
    const value = websiteTitle;
    setLoadingWebsiteTitle(true);
    try {
      await update({
        key: "websiteTitle",
        value,
      });
      updateWebInfo({ title: value });
      toast.success(i18n.t("settings.success"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando websiteTitle:', error);
    } finally {
      setLoadingWebsiteTitle(false);
    }
  }

  async function handleChangeWebsiteDescription(value) {
    setWebsiteDescription(value);
  }

  async function handleSaveWebsiteDescription() {
    const value = websiteDescription;
    setLoadingWebsiteDescription(true);
    try {
      await update({
        key: "websiteDescription",
        value,
      });
      updateWebInfo({ description: value });
      toast.success(i18n.t("settings.success"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando websiteDescription:', error);
    } finally {
      setLoadingWebsiteDescription(false);
    }
  }

  async function handleChangeWebsiteKeyword(value) {
    setWebsiteKeyword(value);
  }

  async function handleSaveWebsiteKeyword() {
    const value = websiteKeyword;
    setLoadingWebsiteKeyword(true);
    try {
      await update({
        key: "websiteKeyword",
        value,
      });
      // ✅ CORREGIDO: Usar 'keywords' (plural) para coincidir con useWebInfo
      updateWebInfo({ keywords: value });
      toast.success(i18n.t("settings.success"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando websiteKeyword:', error);
    } finally {
      setLoadingWebsiteKeyword(false);
    }
  }

  async function handleChangeCompanyName(value) {
    setCompanyName(value);
  }

  async function handleSaveCompanyName() {
    const value = companyName;
    setLoadingCompanyName(true);
    try {
      await update({
        key: "companyName",
        value,
      });
      updateWebInfo({ companyName: value });
      toast.success(i18n.t("settings.success"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando companyName:', error);
    } finally {
      setLoadingCompanyName(false);
    }
  }

  return (
    <Box className={classes.container}>
      {/* CONFIGURACIONES GLOBALES - Solo para Super Usuario */}
      <OnlyForSuperUser
        user={currentUser}
        yes={() => (
          <Card className={classes.sectionCard}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>
                <span role="img" aria-label="Global">🌐</span> {i18n.t("settings.options.globalSettings")}
              </Typography>
            </Box>
            <CardContent className={classes.sectionContent}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <SettingItem
                    label={i18n.t("settings.options.registrationAllowed")}
                    description={i18n.t("settings.options.registrationAllowedDesc")}
                    value={allowregister}
                    onChange={(e) => handleallowregister(e.target.value)}
                    options={[
                      { value: "disabled", label: i18n.t("settings.states.no") },
                      { value: "enabled", label: i18n.t("settings.states.yes") }
                    ]}
                    loading={loadingallowregister}
                    classes={classes}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <SettingItem
                    label={i18n.t("settings.options.registrationVisible")}
                    description={i18n.t("settings.options.registrationVisibleDesc")}
                    value={viewregister}
                    onChange={(e) => handleviewregister(e.target.value)}
                    options={[
                      { value: "disabled", label: i18n.t("settings.states.no") },
                      { value: "enabled", label: i18n.t("settings.states.yes") }
                    ]}
                    loading={loadingviewregister}
                    classes={classes}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <SettingItem
                    label={i18n.t("settings.options.trialTime")}
                    description={i18n.t("settings.options.trialTimeDesc")}
                    value={trial}
                    onChange={(e) => handletrial(e.target.value)}
                    options={[
                      { value: "1", label: `1 ${i18n.t("settings.options.day")}` },
                      { value: "2", label: `2 ${i18n.t("settings.options.days")}` },
                      { value: "3", label: `3 ${i18n.t("settings.options.days")}` },
                      { value: "4", label: `4 ${i18n.t("settings.options.days")}` },
                      { value: "5", label: `5 ${i18n.t("settings.options.days")}` },
                      { value: "6", label: `6 ${i18n.t("settings.options.days")}` },
                      { value: "7", label: `7 ${i18n.t("settings.options.days")}` }
                    ]}
                    loading={loadingtrial}
                    classes={classes}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      />

      {/* CONFIGURACIÓN DE HUB NOTIFICAME */}
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label="Configuraciones">⚙️</span> {i18n.t("settings.options.additionalSettings")}
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  <span role="img" aria-label="Notificaciones">🔔</span> {i18n.t("settings.options.hubNotificaMeToken")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.options.hubNotificaMeTokenDesc")}
                </Typography>
                <TextField
                  id="HubNotificaMe"
                  name="HubNotificaMe"
                  margin="dense"
                  label={i18n.t("settings.options.hubNotificaMeTokenLabel")}
                  variant="outlined"
                  value={hubNotificaMeToken}
                  onChange={(e) => handleChangeHubNotificaMe(e.target.value)}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingHubNotificaMeToken ? i18n.t("settings.states.updating") : i18n.t("settings.options.hubNotificaMeTokenHelper")}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* INFO WEB */}
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label="Información Web">🌐</span> {i18n.t("settings.options.webInfo")}
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.options.websiteTitle")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.options.websiteTitleDesc")}
                </Typography>
                <TextField
                  id="websiteTitle"
                  name="websiteTitle"
                  margin="dense"
                  label={i18n.t("settings.options.websiteTitle")}
                  variant="outlined"
                  value={websiteTitle}
                  onChange={(e) => handleChangeWebsiteTitle(e.target.value)}
                  onBlur={handleSaveWebsiteTitle}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingWebsiteTitle ? i18n.t("settings.states.updating") : i18n.t("settings.options.exampleCliqueaCRM")}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.options.websiteDescription")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.options.websiteDescriptionDesc")}
                </Typography>
                <TextField
                  id="websiteDescription"
                  name="websiteDescription"
                  margin="dense"
                  label={i18n.t("settings.options.websiteDescription")}
                  variant="outlined"
                  value={websiteDescription}
                  onChange={(e) => handleChangeWebsiteDescription(e.target.value)}
                  onBlur={handleSaveWebsiteDescription}
                  fullWidth
                  multiline
                  rows={2}
                  className={classes.textField}
                  helperText={loadingWebsiteDescription ? i18n.t("settings.states.updating") : i18n.t("settings.options.exampleAutomatiza")}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.options.websiteKeyword")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.options.websiteKeywordDesc")}
                </Typography>
                <TextField
                  id="websiteKeyword"
                  name="websiteKeyword"
                  margin="dense"
                  label={i18n.t("settings.options.websiteKeyword")}
                  variant="outlined"
                  value={websiteKeyword}
                  onChange={(e) => handleChangeWebsiteKeyword(e.target.value)}
                  onBlur={handleSaveWebsiteKeyword}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingWebsiteKeyword ? i18n.t("settings.states.updating") : i18n.t("settings.options.exampleWhatsAppAPI")}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.options.companyNameCopyright")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.options.companyNameDesc")}
                </Typography>
                <TextField
                  id="companyName"
                  name="companyName"
                  margin="dense"
                  label={i18n.t("settings.options.companyName")}
                  variant="outlined"
                  value={companyName}
                  onChange={(e) => handleChangeCompanyName(e.target.value)}
                  onBlur={handleSaveCompanyName}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingCompanyName ? i18n.t("settings.states.updating") : i18n.t("settings.options.exampleWatoolx")}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider style={{ margin: "24px 0" }} />

      {/* LOGOS */}
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label="Logos">🖼️</span> {i18n.t("settings.options.logos")}
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent} style={{ padding: "16px" }}>
          <LogoManager />
        </CardContent>
      </Card>
    </Box>
  );
}

